import {
    Body1Strong,
    Button,
    Caption1,
    Title2,
} from "@fluentui/react-components";

import React, { useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import "./../../styles/Chat.css";
import "../../styles/prism-material-oceanic.css";
import "./../../styles/HomeInput.css";

import { HomeInputProps, iconMap, QuickTask } from "../../models/homeInput";
import { TaskService } from "../../services/TaskService";
import { NewTaskService } from "../../services/NewTaskService";
import { RAIErrorCard, RAIErrorData } from "../errors";
import webSocketService from "../../services/WebSocketService";
import { WebsocketMessageType } from "../../models";
import { apiService } from "../../api/apiService";

import ChatInput from "@/coral/modules/ChatInput";
import InlineToaster, { useInlineToaster } from "../toast/InlineToaster";
import PromptCard from "@/coral/components/PromptCard";
import { Send } from "@/coral/imports/bundleicons";
import { Clipboard20Regular } from "@fluentui/react-icons";

// Icon mapping function to convert string icons to FluentUI icons
const getIconFromString = (iconString: string | React.ReactNode): React.ReactNode => {
    // If it's already a React node, return it
    if (typeof iconString !== 'string') {
        return iconString;
    }

    // Check if it's an emoji (simple check for common emoji patterns)
    const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{FE00}-\u{FEFF}]|[\u{1F000}-\u{1FFFF}]/u;
    if (emojiRegex.test(iconString)) {
        // Return emoji as a span element
        return <span style={{ fontSize: '20px' }}>{iconString}</span>;
    }

    return iconMap[iconString] || iconMap['default'] || <Clipboard20Regular />;
};

const truncateDescription = (description: string, maxLength: number = 180): string => {
    if (!description) return '';

    if (description.length <= maxLength) {
        return description;
    }


    const truncated = description.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');

    const cutPoint = lastSpaceIndex > maxLength - 20 ? lastSpaceIndex : maxLength;

    return description.substring(0, cutPoint) + '...';
};

// Extended QuickTask interface to store both truncated and full descriptions
interface ExtendedQuickTask extends QuickTask {
    fullDescription: string; // Store the full, untruncated description
}

const HomeInput: React.FC<HomeInputProps> = ({
    selectedTeam,
}) => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [input, setInput] = useState<string>("");
    const [raiError, setRAIError] = useState<RAIErrorData | null>(null);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const navigate = useNavigate();
    const location = useLocation(); // ✅ location.state used to control focus
    const { showToast, dismissToast } = useInlineToaster();

    useEffect(() => {
        if (location.state?.focusInput) {
            textareaRef.current?.focus();
        }
    }, [location]);

    const resetTextarea = () => {
        setInput("");
        setRAIError(null); // Clear any RAI errors
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.focus();
        }
    };

    useEffect(() => {
        const cleanup = NewTaskService.addResetListener(resetTextarea);
        return cleanup;
    }, []);

    const handleSubmit = async () => {
        if (input.trim()) {
            setSubmitting(true);
            setRAIError(null); // Clear any previous RAI errors
            let id = showToast("Creating a plan", "progress");

            try {
                // Establish WebSocket connection BEFORE creating the plan
                // This ensures we receive the plan approval notification in real-time
                try {
                    await webSocketService.connect(); // Use simple endpoint (no planId needed)
                    console.log("✅ WebSocket connected before plan creation");
                } catch (wsError) {
                    console.warn("⚠️ WebSocket connection failed, continuing without real-time updates:", wsError);
                }

                const response = await TaskService.createPlan(
                    input.trim(),
                    selectedTeam?.team_id
                );
                console.log("Plan created:", response);
                setInput("");

                if (textareaRef.current) {
                    textareaRef.current.style.height = "auto";
                }

                if (response.plan_id && response.plan_id !== null) {
                    showToast("Plan created! Waiting for processing...", "success");
                    dismissToast(id);

                    // Helper: verify plan exists in DB before navigating
                    const verifyAndNavigate = async (planId: string) => {
                        for (let attempt = 0; attempt < 3; attempt++) {
                            try {
                                await apiService.getPlanById(planId, false);
                                // Plan exists — navigate with freshlyCreated flag
                                navigate(`/plan/${planId}`, { state: { freshlyCreated: true } });
                                return;
                            } catch {
                                // Plan not queryable yet, wait 1s and retry
                                await new Promise((r) => setTimeout(r, 1000));
                            }
                        }
                        // After 3 retries the plan still isn't queryable;
                        // navigate anyway — PlanPage has its own retry-with-backoff
                        navigate(`/plan/${planId}`, { state: { freshlyCreated: true } });
                    };

                    let navigated = false;

                    // Listen for PLAN_APPROVAL_REQUEST, then verify existence before navigating
                    const unsubscribe = webSocketService.on(WebsocketMessageType.PLAN_APPROVAL_REQUEST, async (message) => {
                        if (navigated) return;
                        navigated = true;
                        unsubscribe();
                        console.log('📋 Plan approval request received, verifying plan exists before navigating');
                        await verifyAndNavigate(response.plan_id);
                    });

                    // 60-second maximum wait — show a message instead of navigating blindly
                    setTimeout(() => {
                        if (navigated) return;
                        navigated = true;
                        unsubscribe();
                        console.log('⏰ 60s timeout reached — plan creation is taking longer than expected');
                        showToast("Plan creation is taking longer than expected. You can keep waiting or try again.", "warning");
                    }, 60000);
                } else {
                    showToast("Failed to create plan", "error");
                    dismissToast(id);
                }
            } catch (error: any) {
                console.log("Error creating plan:", error);
                let errorMessage = "Unable to create plan. Please try again.";
                dismissToast(id);
                // Check if this is an RAI validation error
                try {
                    // errorDetail = JSON.parse(error);
                    errorMessage = error?.message || errorMessage;
                } catch (parseError) {
                    console.error("Error parsing error detail:", parseError);
                }


                showToast(errorMessage, "error");
            } finally {
                setInput("");
                setSubmitting(false);
            }
        }
    };

    const handleQuickTaskClick = (task: ExtendedQuickTask) => {
        setInput(task.fullDescription);
        setRAIError(null); // Clear any RAI errors when selecting a quick task
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [input]);

    // Convert team starting_tasks to ExtendedQuickTask format
    const tasksToDisplay: ExtendedQuickTask[] = selectedTeam && selectedTeam.starting_tasks ?
        selectedTeam.starting_tasks.map((task, index) => {
            // Handle both string tasks and StartingTask objects
            if (typeof task === 'string') {
                return {
                    id: `team-task-${index}`,
                    title: task,
                    description: truncateDescription(task),
                    fullDescription: task, // Store the full description
                    icon: getIconFromString("📋")
                };
            } else {
                // Handle StartingTask objects
                const startingTask = task as any; // Type assertion for now
                const taskDescription = startingTask.prompt || startingTask.name || 'Task description';
                return {
                    id: startingTask.id || `team-task-${index}`,
                    title: startingTask.name || startingTask.prompt || 'Task',
                    description: truncateDescription(taskDescription),
                    fullDescription: taskDescription, // Store the full description
                    icon: getIconFromString(startingTask.logo || "📋")
                };
            }
        }) : [];

    return (
        <div className="home-input-container">
            <div className="home-input-content">
                <div className="home-input-center-content">
                    <div className="home-input-title-wrapper">
                        <Title2>{selectedTeam?.ui_config?.greeting || "What exception should Nolij resolve?"}</Title2>
                    </div>

                    {/* Show RAI error if present */}
                    {/* {raiError && (
                        <RAIErrorCard
                            error={raiError}
                            onRetry={() => {
                                setRAIError(null);
                                if (textareaRef.current) {
                                    textareaRef.current.focus();
                                }
                            }}
                            onDismiss={() => setRAIError(null)}
                        />
                    )} */}

                    <ChatInput
                        ref={textareaRef} // forwarding
                        value={input}
                        placeholder={selectedTeam?.ui_config?.placeholder || "Describe your task..."}
                        onChange={setInput}
                        onEnter={handleSubmit}
                        disabledChat={submitting}
                        agents={selectedTeam?.agents?.map(agent => ({
                            name: agent.name || 'Agent',
                            icon: agent.icon
                        }))}
                    >
                        <Button
                            appearance="subtle"
                            className="home-input-send-button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            icon={<Send />}
                        />
                    </ChatInput>

                    <InlineToaster />

                    <div className="home-input-quick-tasks-section">
                        {tasksToDisplay.length > 0 && (
                            <>
                                <div className="home-input-quick-tasks-header">
                                    <Body1Strong>Quick tasks</Body1Strong>
                                </div>

                                <div className="home-input-quick-tasks">
                                    {tasksToDisplay.map((task) => (
                                        <PromptCard
                                            key={task.id}
                                            title={task.title}
                                            icon={task.icon}
                                            description={task.description}
                                            onClick={() => handleQuickTaskClick(task)}
                                            disabled={submitting}

                                        />
                                    ))}
                                </div>
                            </>
                        )}
                        {tasksToDisplay.length === 0 && selectedTeam && (
                            <div style={{
                                textAlign: 'center',
                                padding: '32px 16px',
                                color: '#666'
                            }}>
                                <Caption1>No starting tasks available for this team</Caption1>
                            </div>
                        )}
                        {!selectedTeam && (
                            <div style={{
                                textAlign: 'center',
                                padding: '32px 16px',
                                color: '#666'
                            }}>
                                <Caption1>Select a team to see available tasks</Caption1>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomeInput;